import { useEffect, useMemo, useRef } from "react";
import {
  AccessibilityInfo,
  findNodeHandle,
  Platform,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import {
  ATHLETE_TIME_ZONES,
  proposeTimeZoneRegion,
  type AthleteTimeZone,
} from "@heraion/shared";

import { Chip } from "../primitives/Chip";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { LoadingIndicator } from "../primitives/LoadingIndicator";
import { useDisclosure } from "../primitives/useDisclosure";
import { makeSettingsCardStyles } from "./settingsCardStyles";
import type { UiTheme } from "../theme";

// #510: the athlete picks a region from a short list, never a city. The
// mount saves `Profile.timeZone`; "Not set" saves it absent, and the API
// keeps counting days in UTC. Closed at rest (one line and "Change"); the
// list opens in place in the same card (#513: no sub-page), and closes again
// once the pick is saved.
// 30/10: with nothing set, the zone the device declares is PROPOSED under
// the value, with "Confirm" beside it. Nothing is saved until that tap, and
// once a region is set the device is not read again (travelling changes
// nothing). The mount passes the zone in: this card never reads Intl's.

const NOT_SET = "";
type Choice = AthleteTimeZone | typeof NOT_SET;
const CHOICES: readonly Choice[] = [NOT_SET, ...ATHLETE_TIME_ZONES.map((z) => z.id)];
const REGION_OF = new Map<string, string>(ATHLETE_TIME_ZONES.map((z) => [z.id, z.region]));

export function TimeZoneChoice({
  theme,
  value,
  onPick,
  saving = false,
  error = null,
  deviceTimeZone,
}: {
  theme: UiTheme;
  /** The athlete's pick; undefined = not set. */
  value: AthleteTimeZone | undefined;
  /** The new pick; undefined = not set. */
  onPick: (next: AthleteTimeZone | undefined) => void;
  saving?: boolean;
  error?: string | null;
  /** The IANA zone the device declares (`deviceTimeZone()`); undefined =
   *  unknown, nothing proposed. */
  deviceTimeZone?: string;
}) {
  const { t } = useTranslation();
  const cs = useMemo(() => makeSettingsCardStyles(theme), [theme]);
  const local = useMemo(() => makeStyles(theme), [theme]);
  const { expanded, toggle, setExpanded } = useDisclosure(false);
  const groupRef = useRef<View>(null);
  const current: Choice = value ?? NOT_SET;
  const proposed = useMemo(
    () => (value === undefined ? proposeTimeZoneRegion(deviceTimeZone) : null),
    [value, deviceTimeZone],
  );
  const labelOf = (choice: Choice) => {
    const region = REGION_OF.get(choice);
    return region
      ? t(`mobile.prefs.timeZoneRegion.${region}`, { defaultValue: choice })
      : t("mobile.prefs.timeZoneNone", { defaultValue: "Not set (UTC)" });
  };

  // A saved pick comes back as a new `value`: the list closes. A failed one
  // leaves `value` alone, so the list stays open beside its error.
  useEffect(() => {
    setExpanded(false);
  }, [value, setExpanded]);

  // The reader lands on the list it just opened. Native only, as in
  // AgeDeclarationOverlay: on web the chips follow the button in tab order.
  useEffect(() => {
    if (!expanded || Platform.OS === "web") return;
    const tag = groupRef.current === null ? null : findNodeHandle(groupRef.current);
    if (tag !== null) AccessibilityInfo.setAccessibilityFocus(tag);
  }, [expanded]);

  const label = t("mobile.prefs.timeZoneLabel", { defaultValue: "Time zone" });
  const confirm = t("mobile.prefs.timeZoneConfirm", { defaultValue: "Confirm" });
  return (
    <View style={cs.card}>
      <View style={local.head}>
        <View style={local.headText}>
          <Text style={cs.cardLabel}>{label}</Text>
          <Text style={cs.cardValue}>{labelOf(current)}</Text>
        </View>
        {/* In the head row: a pick far down the open list still shows it saves. */}
        {saving ? <LoadingIndicator theme={theme} /> : null}
        <Chip
          theme={theme}
          prominent
          label={
            expanded
              ? t("mobile.common.close", { defaultValue: "Close" })
              : t("mobile.prefs.timeZoneChange", { defaultValue: "Change" })
          }
          accessibilityLabel={`${
            expanded
              ? t("mobile.common.close", { defaultValue: "Close" })
              : t("mobile.prefs.timeZoneChange", { defaultValue: "Change" })
          }: ${label}`}
          expanded={expanded}
          onPress={toggle}
        />
      </View>
      {proposed !== null && !expanded ? (
        <View style={local.proposalRow}>
          <Text style={[cs.cardHelp, local.proposal]}>
            {t("mobile.prefs.timeZoneProposal", {
              defaultValue: "{{region}}, from your device",
              region: labelOf(proposed),
            })}
          </Text>
          <Chip
            theme={theme}
            prominent
            label={confirm}
            accessibilityLabel={`${confirm}: ${labelOf(proposed)}`}
            disabled={saving}
            style={local.confirm}
            // No slop upward: Change sits right above, and a stray tap there
            // must not SAVE. 36 + 8 below is still the 44 floor.
            hitSlop={{ top: 0, bottom: theme.spacing.s3, left: theme.spacing.s3, right: theme.spacing.s3 }}
            onPress={() => {
              if (!saving) onPick(proposed);
            }}
          />
        </View>
      ) : null}
      {error ? (
        // RNW forwards both to the DOM: role="alert", aria-live="polite".
        <Text style={local.error} accessibilityRole="alert" accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}
      {expanded ? (
        <>
          <Text style={cs.cardHelp}>
            {t("mobile.prefs.timeZoneHelp", {
              defaultValue: "Pick the region where you train. Not set means UTC.",
            })}
          </Text>
          <ChipChoiceRow<Choice>
            theme={theme}
            choices={CHOICES}
            selected={current}
            onSelect={(next) => {
              if (saving || next === current) return;
              onPick(next === NOT_SET ? undefined : next);
            }}
            label={labelOf}
            chipA11yLabel={labelOf}
            isDisabled={() => saving}
            groupA11yLabel={label}
            wrapLabels
            groupRef={groupRef}
          />
        </>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    head: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    headText: { flex: 1, minWidth: 0, gap: s.s1 },
    // CuePill's rule: the line keeps ~14 characters beside Confirm and wraps
    // there; narrower than that (320 px, 1.5x, Russian), Confirm drops to
    // the next line, right-aligned, instead of squeezing the line until a
    // word breaks. `flexBasis: 0` makes that floor, not the full text, the
    // width the wrap decision sees.
    // marginTop: Change's touch area (8 of slop below it) and Confirm's stay
    // apart, by 8 or more, on native and on web (no slop there, 44 boxes).
    proposalRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: s.s3,
      marginTop: s.s4,
    },
    proposal: { flexGrow: 1, flexBasis: 0, minWidth: theme.typography.sizes.sm * 14 },
    confirm: { marginLeft: "auto" },
    error: { color: theme.palette.danger, fontSize: theme.typography.sizes.base },
  });
}
